import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Button, Modal, Alert } from "@mui/material";
import Div from "@jumbo/shared/Div";
import { Add as AddIcon } from "@mui/icons-material";
import ProductSelector from "./ProductSelector";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  sortableKeyboardCoordinates,
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import CategoryProductItemSortable from "./CategoryProductItemSortable";
import JumboListNoDataPlaceholder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";

const CategoryProductsTab = ({
  category,
  products,
  onEditCategory,
  defaultLanguage,
  currencyCode = "",
}) => {
  const { t } = useTranslation();
  const [productModal, setProductModal] = useState();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id && category?.products) {
      const oldIndex = Object.keys(category.products).indexOf(active.id);
      const newIndex = Object.keys(category.products).indexOf(over.id);

      const reorderedProducts = {};
      const productKeys = Object.keys(category.products);
      const newOrder = arrayMove(productKeys, oldIndex, newIndex);

      newOrder.forEach((productId) => {
        reorderedProducts[productId] = category.products[productId];
      });

      const updatedCategory = {
        ...category,
        products: reorderedProducts,
      };

      onEditCategory(updatedCategory);
    }
  };

  const handleAddProduct = () => {
    setProductModal({ type: "addProduct" });
  };

  const handleRemoveProduct = (productId) => {
    if (category?.products) {
      const updatedProducts = { ...category.products };
      delete updatedProducts[productId];

      const updatedCategory = {
        ...category,
        products: updatedProducts,
      };

      onEditCategory(updatedCategory);
    }
  };

  if (!category) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="body1" color="text.secondary">
          {t("common.loading")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
        <Alert variant="outlined" severity="info">
          {t("pages.dsokCategories.addProductsInfo")}
        </Alert>
      </Typography>
      <Div
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          pb: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddProduct}
          size="small"
        >
          {t("buttons.addProduct")}
        </Button>
      </Div>

      {(() => {
        const hasProducts =
          category?.products && Object.keys(category.products).length > 0;
        return hasProducts;
      })() ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={Object.keys(category.products)}
            strategy={verticalListSortingStrategy}
          >
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
            >
              {Object.keys(category.products).map((productId) => {
                const product = category.products[productId];

                return (
                  <CategoryProductItemSortable
                    key={productId}
                    productId={productId}
                    product={product}
                    onRemove={handleRemoveProduct}
                    defaultLanguage={defaultLanguage}
                    products={products}
                    currencyCode={currencyCode}
                  />
                );
              })}
            </Box>
          </SortableContext>
        </DndContext>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 1,
            p: 4,
            mt: 2,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            <JumboListNoDataPlaceholder />
          </Typography>
        </Box>
      )}

      {/* Product Selection Modal */}
      <Modal
        open={productModal !== undefined}
        onClose={() => setProductModal()}
      >
        <Div
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: "75vw",
            maxHeight: "70vh",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            overflow: "auto",
          }}
        >
          {productModal?.type === "addProduct" && (
            <ProductSelector
              setFieldValue={(field, value) => {
                if (field === "products") {
                  // Get current products from the category
                  const currentProducts = category?.products || {};

                  // Ensure value is an object with product IDs as keys
                  const productsToAdd =
                    typeof value === "object" && value !== null ? value : {};

                  // Merge current products with new products
                  const updatedProducts = {
                    ...currentProducts,
                    ...productsToAdd,
                  };

                  // Create updated category with merged products
                  const updatedCategory = {
                    ...category,
                    products: updatedProducts,
                  };

                  // Update the category
                  onEditCategory(updatedCategory);
                }
              }}
              values={category || {}}
              setCloseModal={() => setProductModal()}
              categoryId={category?._id}
              products={Array.isArray(products) ? products : []}
            />
          )}
        </Div>
      </Modal>
    </Box>
  );
};

export default CategoryProductsTab;
